// GSAP Scroll Animations — Lazy-loaded version
// GSAP / ScrollTrigger / ScrollSmoother load on demand when an element that
// needs them approaches the viewport. URLs are passed via data-attrs on this
// script's <script> tag (set in layout/theme.liquid).

(function () {
  const me = document.currentScript || document.querySelector('script[src*="main.js"]');
  const gsapUrls = me
    ? [me.dataset.gsapUrl, me.dataset.stUrl, me.dataset.ssUrl].filter(Boolean)
    : [];

  let gsapLoading = null;
  function loadGsapSuite() {
    if (gsapLoading) return gsapLoading;
    if (
      typeof gsap !== "undefined" &&
      typeof ScrollTrigger !== "undefined" &&
      typeof ScrollSmoother !== "undefined"
    ) {
      gsapLoading = Promise.resolve();
      return gsapLoading;
    }
    if (gsapUrls.length === 0) return Promise.resolve();

    gsapLoading = (async () => {
      for (const url of gsapUrls) {
        await new Promise((resolve, reject) => {
          const tag = document.createElement("script");
          tag.src = url;
          tag.onload = resolve;
          tag.onerror = reject;
          document.head.appendChild(tag);
        });
      }
    })();
    return gsapLoading;
  }

  // ---------- Animation setup (runs AFTER GSAP is loaded) ----------

  function setupGlobalScroll() {
    const elementsWithSpeed = document.querySelectorAll("[data-speed]");
    const smoothWrapper = document.getElementById("smooth-wrapper");
    if (!smoothWrapper) return;

    if (elementsWithSpeed.length > 0) {
      ScrollSmoother.create({
        smooth: 0.8,
        ignoreMobileResize: true,
        effects: true,
      });
      smoothWrapper.classList.add("is-active");
    } else {
      smoothWrapper.classList.remove("is-active");
    }
  }

  function setupStarAnimation() {
    document.querySelectorAll(".js-stars").forEach((container) => {
      container.querySelectorAll(".star").forEach((star) => {
        gsap.to(star, {
          duration: 1,
          opacity: 1,
          stagger: 0.2,
          repeat: -1,
          yoyo: true,
        });
        gsap.fromTo(
          star,
          {
            opacity: 0.8,
            scale: 0.5,
            transformOrigin: "50% 50%",
          },
          {
            duration: 3.5,
            delay: Math.random() * 3,
            opacity: 1,
            scale: 1,
            repeat: -1,
            yoyo: true,
            stagger: 1,
          }
        );
      });
    });
  }

  function setupProductAnimation() {
    const productImages = document.querySelectorAll(
      ".js-custom-animation-product-main-image"
    );
    if (productImages.length === 0) return;

    productImages.forEach((image) => {
      gsap.timeline({
        scrollTrigger: {
          trigger: ".js-custom-animation-product",
          scrub: 1,
          start: "top 25%",
          end: "bottom 75%",
          markers: false,
        },
      }).to(image, {
        stagger: 0.5,
        rotation: 16,
        xPercent: -25,
      });
    });
  }

  function setupResizeHandlers() {
    const refresh = () =>
      requestAnimationFrame(() => ScrollTrigger.refresh());
    let last = 0;
    const throttled = () => {
      const now = Date.now();
      if (now - last > 250) {
        last = now;
        refresh();
      }
    };
    window.addEventListener("resize", throttled, { passive: true });
    new ResizeObserver(throttled).observe(document.body);
    setTimeout(refresh, 1000);
  }

  // ---------- Lazy-trigger orchestration ----------

  let inited = false;
  async function initGsapAnimations() {
    if (inited) return;
    inited = true;
    try {
      await loadGsapSuite();
    } catch (e) {
      // Silent fail — animations decorative
      return;
    }
    if (typeof gsap === "undefined") return;
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
    setupGlobalScroll();
    setupStarAnimation();
    setupProductAnimation();
    setupResizeHandlers();
  }

  function setupLazyTrigger() {
    const selectors = [
      ".js-stars",
      "[data-speed]",
      ".js-custom-animation-product-main-image",
    ];
    const targets = [];
    selectors.forEach((sel) =>
      document.querySelectorAll(sel).forEach((el) => targets.push(el))
    );
    if (targets.length === 0) return;

    if (!("IntersectionObserver" in window)) {
      // Old browser fallback — load eagerly
      initGsapAnimations();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            io.disconnect();
            initGsapAnimations();
            return;
          }
        }
      },
      { rootMargin: "300px 0px" }
    );
    targets.forEach((t) => io.observe(t));
  }

  // ---------- Swiper slider (only inits if Swiper is loaded on this page) ----------

  function initSlider() {
    if (typeof Swiper === "undefined") return;

    document.querySelectorAll(".custom-slider").forEach(function (section) {
      const customSlider = section.querySelector(".custom-slider-main");
      if (!customSlider) return;
      const prevBtn = section.querySelector(".swiper-button-prev");
      const nextBtn = section.querySelector(".swiper-button-next");

      const items = Number(customSlider.getAttribute("data-items"));
      const itemsMd = Number(customSlider.getAttribute("data-items-md"));
      const itemsLg = Number(customSlider.getAttribute("data-items-lg"));
      const items3xl = Number(customSlider.getAttribute("data-items-3xl"));

      new Swiper(customSlider, {
        navigation: { nextEl: nextBtn, prevEl: prevBtn },
        speed: 700,
        spaceBetween: 24,
        slidesPerGroup: items,
        slidesPerView: items,
        breakpoints: {
          320: { slidesPerView: items, slidesPerGroup: items },
          767: { slidesPerView: itemsMd, slidesPerGroup: itemsMd },
          991: { slidesPerView: itemsLg, slidesPerGroup: itemsLg },
          1640: { slidesPerView: items3xl, slidesPerGroup: items3xl },
        },
      });
    });
  }

  // ---------- Contact type select (no library deps) ----------

  function handleContactTypeChange() {
    const sel = document.querySelector("#contact-type");
    if (!sel) return;

    const showForm = (id) => {
      document
        .querySelectorAll(".contact-type-form")
        .forEach((f) => (f.style.display = "none"));
      const target = document.querySelector("#" + id);
      if (target) target.style.display = "block";
    };

    if (sel.value) showForm(sel.value);
    sel.addEventListener("change", (e) => showForm(e.target.value));
  }

  // ---------- Boot ----------

  function init() {
    setupLazyTrigger();
    initSlider();
    handleContactTypeChange();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
